import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerDepartmentValidation } from '../hooks/useServerDepartmentValidation';
import { useUpsertDepartmentForm } from '../hooks/useUpsertDepartmentForm';
import type {
	DepartmentDto,
	UpsertDepartmentDto,
} from '../types/DepartmentDto';
import { UpsertDepartmentForm } from './UpsertDepartmentForm';
import { Title } from '../../../components/Title';

export type NewDepartmentModalProps = {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
};

export function NewDepartmentModal({
	isOpened,
	close,
	triggerListUpdate,
}: NewDepartmentModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertDepartmentForm();

	const newDepartmentMutation = mutate<UpsertDepartmentDto, DepartmentDto>(
		`${organisation}/departments/add`,
		inputs,
	);

	useServerDepartmentValidation(newDepartmentMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'New Department'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={newDepartmentMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Department Added!'}
					body={'A new department has been added...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newDepartmentMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertDepartmentForm form={form} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
